<script setup lang="ts">
import { computed } from 'vue'
import { micromark } from 'micromark'
import { type Account, TheTooltip } from '@wastedondestiny/destiny-library'
import { BeyondLightSeasonHashes, EdgeOfFateSeasonHashes, FinalShapeEpisodeHashes, LightfallSeasonHashes, RenegadesSeasonHashes, ShadowkeepSeasonHashes, WitchQueenSeasonHashes } from '../enums/seasons'
import { releaseDetail } from '../releaseDetail'

const props = withDefaults(defineProps<{
  season: number,
  account: Account,
  noInteract?: boolean
}>(), {
  noInteract: false
})

const owns = computed(() => props.account.seasonsOwned?.includes(props.season))
const seasonDescription = computed(() => micromark(releaseDetail.get(props.season)?.shortDescription ?? ''))
</script>

<template>
  <div class="h-6 w-6 flex justify-center">
    <TheTooltip v-if="$props.season === ShadowkeepSeasonHashes.Undying" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/undying.svg" alt="Season of the Undying" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/undying.svg" alt="Season of the Undying" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === ShadowkeepSeasonHashes.Dawn" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/dawn.svg" alt="Season of Dawn" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/dawn.svg" alt="Season of Dawn" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === ShadowkeepSeasonHashes.Worthy" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/worthy.svg" alt="Season of the Worthy" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/worthy.svg" alt="Season of the Worthy" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === ShadowkeepSeasonHashes.Arrivals" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/arrivals.svg" alt="Season of Arrivals" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/arrivals.svg" alt="Season of Arrivals" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === BeyondLightSeasonHashes.Hunt" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/hunt.svg" alt="Season of the Hunt" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/hunt.svg" alt="Season of the Hunt" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === BeyondLightSeasonHashes.Chosen" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/chosen.svg" alt="Season of the Chosen" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/chosen.svg" alt="Season of the Chosen" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === BeyondLightSeasonHashes.Splicer" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/splicer.png" alt="Season of the Splicer" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/splicer.png" alt="Season of the Splicer" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === BeyondLightSeasonHashes.Lost" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/lost.svg" alt="Season of the Lost" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/lost.svg" alt="Season of the Lost" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === WitchQueenSeasonHashes.Risen" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/risen.svg" alt="Season of the Risen" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/risen.svg" alt="Season of the Risen" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === WitchQueenSeasonHashes.Haunted" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/haunted.png" alt="Season of the Haunted" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/haunted.png" alt="Season of the Haunted" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === WitchQueenSeasonHashes.Plunder" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/plunder.png" alt="Season of the Plunder" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/plunder.png" alt="Season of the Plunder" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === WitchQueenSeasonHashes.Seraph" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/seraph.svg" alt="Season of the Seraph" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/seraph.svg" alt="Season of the Seraph" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === LightfallSeasonHashes.Defiance" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/defiance.svg" alt="Season of Defiance" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/defiance.svg" alt="Season of Defiance" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === LightfallSeasonHashes.Deep" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/deep.svg" alt="Season of the Deep" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/deep.svg" alt="Season of the Deep" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === LightfallSeasonHashes.Witch" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/witch.svg" alt="Season of the Witch" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/witch.svg" alt="Season of the Witch" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === LightfallSeasonHashes.Wish" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/wish.svg" alt="Season of the Wish" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/wish.svg" alt="Season of the Wish" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === FinalShapeEpisodeHashes.Echoes" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/echoes.svg" alt="Episode: Echoes" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/echoes.svg" alt="Episode: Echoes" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this episode</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === FinalShapeEpisodeHashes.Revenant" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/revenant.svg" alt="Episode: Revenant" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/revenant.svg" alt="Episode: Revenant" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this episode</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === FinalShapeEpisodeHashes.Heresy" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/heresy.svg" alt="Episode: Heresy" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/heresy.svg" alt="Episode: Heresy" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this episode</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === EdgeOfFateSeasonHashes.Reclamation" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/reclamation.png" alt="Season: Reclamation" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/reclamation.png" alt="Season: Reclamation" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === EdgeOfFateSeasonHashes.AshAndIron" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/ash_and_iron.png" alt="Season: Ash and Iron" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/ash_and_iron.png" alt="Season: Ash and Iron" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === RenegadesSeasonHashes.Lawless" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/lawless.svg" alt="Season: Lawless" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/lawless.svg" alt="Season: Lawless" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
    <TheTooltip v-if="$props.season === RenegadesSeasonHashes.MonumentOfTriumph" :warning="!owns" :no-interact="noInteract">
      <img src="../assets/expansions/monument_of_triumph.png" alt="Monument of Triumph" class="mt-1.5 h-5 w-5" :class="{ 'opacity-75': owns, 'opacity-25': !owns }" />
      <template #icon>
        <img src="../assets/expansions/monument_of_triumph.png" alt="Monument of Triumph" class="h-6 w-6" />
      </template>
      <template #content>
        <p>This user {{ owns ? 'owns' : 'does not own' }} this season</p>
        <div class="markdown" v-html="seasonDescription" />
      </template>
    </TheTooltip>
  </div>
</template>